import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

function ProductUnitsIndex() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const query = searchParams.get('q') || '';
    const statusFilter = searchParams.get('status') || '';

    useEffect(() => {
        loadUnits();
    }, [query, statusFilter]);

    const loadUnits = async () => {
        try {
            setLoading(true);
            const response = await api.get('/product_units', { q: query, status: statusFilter });
            if (response.success) {
                setUnits(response.data);
            } else {
                setError(response.message || 'Failed to load units');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const q = e.target.value;
        setSearchParams(prev => {
            if (q) prev.set('q', q);
            else prev.delete('q');
            return prev;
        });
    };

    const handleStatusChange = (e) => {
        const status = e.target.value;
        setSearchParams(prev => {
            if (status) prev.set('status', status);
            else prev.delete('status');
            return prev;
        });
    };

    const statusColors = {
        'available': 'bg-emerald-100 text-emerald-800',
        'sold': 'bg-blue-100 text-blue-800',
        'reserved': 'bg-amber-100 text-amber-800'
    };

    return (
        <div className="mt-16 pb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Product Units</h1>
                    <p className="text-slate-500 mt-1">Track individual serialized items (IMEI/Serial numbers)</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative flex-1 w-full">
                        <input
                            type="text"
                            value={query}
                            onChange={handleSearchChange}
                            placeholder="Search by Serial No / IMEI..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all"
                        />
                        <div className="absolute left-3 top-2.5 text-slate-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    <select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        className="w-full sm:w-auto border border-slate-200 rounded-xl py-2 pl-3 pr-8 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                    >
                        <option value="">All Statuses</option>
                        <option value="available">Available</option>
                        <option value="sold">Sold</option>
                        <option value="reserved">Reserved</option>
                    </select>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-slate-500 font-medium italic">Loading units...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">Product</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">Identifiers</th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">Created At</th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-slate-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {units.map(unit => (
                                    <tr key={unit.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-800">{unit.product_name}</div>
                                            <div className="text-xs text-slate-500">SKU: {unit.product_sku || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                {unit.identifiers && unit.identifiers.map((ident: any, idx: number) => (
                                                    <div key={idx} className={idx === 0 ? "text-sm font-mono text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block w-max" : "text-xs font-mono text-slate-400 w-max"}>
                                                        <span className="font-bold mr-1">{ident.name}:</span>{ident.value}
                                                    </div>
                                                ))}
                                                {(!unit.identifiers || unit.identifiers.length === 0) && (
                                                    <div className="text-sm font-mono text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block">{unit.primary_identifier}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColors[unit.status] || 'bg-slate-100 text-slate-600'}`}>
                                                {unit.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                                            {new Date(unit.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link to={`/product_units/${unit.id}`} className="text-indigo-600 hover:text-indigo-800 font-bold text-xs uppercase tracking-widest">
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                {units.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium italic">
                                            No product units found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductUnitsIndex;
